<template>
  <div v-if="props.videoId" ref="playerContainer" class="video-container" :aria-label="`watch video of ${seoAlt}`">
    <video
      :id="`video_${props.videoId}`"
      ref="player"
      v-bind="videoParams"
      data-embed="default"
      data-application-id
      class="video-js vjs-main_video"
      controls
      draggable="false"
      @play="handleVideoPlay"
      @pause="handleVideoNotPlaying"
    >
      <track kind="captions" />
    </video>
  </div>
</template>

<script setup>
import { ref, watch, toValue } from 'vue';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { getSeoAlt } from '~/utils/getSeoAlt';
import { useBrand } from '~/composables/useBrand';
import { useEnvProps } from '~/composables/useEnvProps';
import { loadScript } from '~/utils/loadScript';
import tagVideo from '~/features/analytics/video';

const props = defineProps({
  videoId: {
    type: String,
    required: true,
  },
  isVideoSelected: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const { productId } = useProduct();
const { brightcoveAccountId } = useEnvProps();
const { productName, brandName } = useCommonProductGetters();

// added default duration of the video to avoid NaN in tests
const DEFAULT_DURATION = 60;
const seoAlt = getSeoAlt(toValue(productName), toValue(brandName));
const player = ref({
  currentTime: 0,
  duration: DEFAULT_DURATION,
});
const playerContainer = ref(null);
const utagData = {
  video_id: [props.videoId],
  product_id: [productId],
  product_name: [toValue(productName)],
};
const { isMcom } = useBrand();
const videoParams = {
  'data-account': brightcoveAccountId,
  'data-video-id': isMcom ? `ref:${props.videoId}` : props.videoId,
  'data-player': isMcom ? '9878QwHBX' : 'default',
};

const handleVideoPlay = () => {
  tagVideo(
    {
      videoTimeViewed: player.value.currentTime,
      videoDuration: player.value.duration || DEFAULT_DURATION,
      data: utagData,
    },
    'start',
  );
};
const handleVideoNotPlaying = () => {
  const { currentTime, duration } = player.value;
  const videoDuration = duration || DEFAULT_DURATION;
  if (currentTime !== videoDuration) {
    tagVideo(
      {
        videoTimeViewed: currentTime,
        videoDuration,
        data: utagData,
      },
      'pause',
    );
  }
};

const loadVideoPlayer = async () => {
  await loadScript(`https://players.brightcove.net/${videoParams['data-account']}/${videoParams['data-player']}_default/index.min.js`);
  const videoContainer = playerContainer.value.querySelectorAll('.vjs-main_video');
  if (videoContainer) {
    videoContainer.forEach((v) => v.setAttribute('draggable', 'false'));
  }
};

watch(
  () => props.isVideoSelected,
  (isVideoSelected) => {
    if (isVideoSelected) {
      loadVideoPlayer();
    }
  },
);
</script>

<style lang="scss" scoped>
.video-container {
  width: 100%;
  height: 100%;
}

.vjs-main_video {
  width: 100%;
  height: 100%;
  user-select: none;
}
</style>
